/**
 * UI Settings Store - Zustand persist store for UI preferences.
 * Schema defined in the ABAC architecture document.
 * Fully typed (TS) — persisted subset is `{ settings }`.
 */

import { STORAGE_KEYS } from "@lib/config/storageKeys";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";

// ─── Types ─────────────────────────────────────────────────────────────────────

export type ThemeMode = "light" | "dark" | "system";
export type FontSize = "small" | "normal" | "large";
export type DashboardLayout = "grid" | "list" | "compact";
export type DashboardPeriod = "today" | "week" | "month" | "year";
export type TableDensity = "compact" | "comfortable" | "wide";
export type BadgeSize = "xs" | "sm" | "md";
export type IconSize = "xs" | "sm" | "md";

export interface SortConfig {
  field: string;
  order: "asc" | "desc";
}

export interface TableConfig {
  defaultPageSize: number;
  defaultSort: SortConfig;
  showRowNumbers: boolean;
  stickyHeader: boolean;
  animation: boolean;
  density: TableDensity;
  badgeSize: BadgeSize;
  iconSize: IconSize;
}

export interface ModuleTableConfig {
  defaultPageSize?: number;
  defaultSort?: SortConfig;
  showRowNumbers?: boolean;
  stickyHeader?: boolean;
  animation?: boolean;
  density?: TableDensity;
  badgeSize?: BadgeSize;
  iconSize?: IconSize;
}

export interface FilterEntry {
  module: string;
  path: string;
  params: Record<string, unknown>;
  timestamp: number;
}

export interface RecentPage {
  path: string;
  label: string;
  module: string;
  count: number;
  lastVisited: number;
}

export interface WidgetConfig {
  visible: boolean;
  order: number;
}

export interface UISettings {
  version?: number;
  updatedAt: string;
  theme: {
    mode: ThemeMode;
    resolved: "light" | "dark";
  };
  navigation: {
    sidebar: {
      collapsed: boolean;
      searchQuery: string;
      favorites: string[];
      activeModuleId: string | null;
    };
    breadcrumbs: {
      expanded: boolean;
    };
  };
  tables: {
    global: TableConfig;
    perModule: Record<string, ModuleTableConfig>;
  };
  filters: {
    recent: FilterEntry[];
  };
  recentPages: RecentPage[];
  dashboard: {
    layout: DashboardLayout;
    period: DashboardPeriod;
    widgets: Record<string, WidgetConfig>;
  };
  accessibility: {
    fontSize: FontSize;
    reducedMotion: boolean;
    highContrast: boolean;
    focusVisible: boolean;
  };
  notifications: {
    sound: boolean;
    desktop: boolean;
    duration: number;
    modules: Record<string, boolean>;
  };
  planning: {
    lastPersonneId: number | null;
  };
}

export interface UISettingsStore {
  settings: UISettings;
  setThemeMode: (mode: ThemeMode) => void;
  resolveTheme: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  setSidebarSearchQuery: (query: string) => void;
  toggleFavorite: (itemId: string) => void;
  setActiveModuleId: (moduleId: string | null) => void;
  setBreadcrumbsExpanded: (expanded: boolean) => void;
  setGlobalPageSize: (pageSize: number) => void;
  setGlobalSort: (sort: SortConfig) => void;
  setShowRowNumbers: (showRowNumbers: boolean) => void;
  setStickyHeader: (stickyHeader: boolean) => void;
  setTableAnimation: (animation: boolean) => void;
  setTableDensity: (density: TableDensity) => void;
  setBadgeSize: (badgeSize: BadgeSize) => void;
  setIconSize: (iconSize: IconSize) => void;
  setModuleTableConfig: (module: string, config: Partial<TableConfig>) => void;
  addRecentFilter: (filter: FilterEntry) => void;
  setRecentFilters: (filters: FilterEntry[]) => void;
  setRecentPages: (pages: RecentPage[]) => void;
  setDashboardLayout: (layout: DashboardLayout) => void;
  setDashboardPeriod: (period: DashboardPeriod) => void;
  setWidgetConfig: (widgetId: string, config: Partial<WidgetConfig>) => void;
  setFontSize: (fontSize: FontSize) => void;
  setReducedMotion: (reducedMotion: boolean) => void;
  setHighContrast: (highContrast: boolean) => void;
  setFocusVisible: (focusVisible: boolean) => void;
  setNotificationSound: (sound: boolean) => void;
  setNotificationDesktop: (desktop: boolean) => void;
  setNotificationDuration: (duration: number) => void;
  setModuleNotification: (module: string, enabled: boolean) => void;
  setLastPersonneId: (personneId: number | null) => void;
  reset: () => void;
}

// ─── Defaults ──────────────────────────────────────────────────────────────────

const defaultUISettings: UISettings = {
  version: 1,
  updatedAt: new Date().toISOString(),
  theme: {
    mode: "system",
    resolved: "light",
  },
  navigation: {
    sidebar: {
      collapsed: false,
      searchQuery: "",
      favorites: [],
      activeModuleId: null,
    },
    breadcrumbs: {
      expanded: true,
    },
  },
  tables: {
    global: {
      defaultPageSize: 25,
      defaultSort: { field: "id", order: "desc" },
      showRowNumbers: false,
      stickyHeader: true,
      animation: true,
      density: "comfortable",
      badgeSize: "sm",
      iconSize: "sm",
    },
    perModule: {},
  },
  filters: {
    recent: [],
  },
  recentPages: [],
  dashboard: {
    layout: "grid",
    period: "month",
    widgets: {},
  },
  accessibility: {
    fontSize: "normal",
    reducedMotion: false,
    highContrast: false,
    focusVisible: true,
  },
  notifications: {
    sound: true,
    desktop: false,
    duration: 3000,
    modules: {},
  },
  planning: {
    lastPersonneId: null,
  },
};

// ─── Store ─────────────────────────────────────────────────────────────────────

export const useUISettings = create<UISettingsStore>()(
  persist(
    immer((set) => ({
      settings: { ...defaultUISettings },

      setThemeMode: (mode: ThemeMode) =>
        set((state) => {
          state.settings.theme.mode = mode;
          state.settings.updatedAt = new Date().toISOString();
        }),

      resolveTheme: () =>
        set((state) => {
          const { mode } = state.settings.theme;
          if (mode === "system") {
            const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
            state.settings.theme.resolved = prefersDark ? "dark" : "light";
          } else {
            state.settings.theme.resolved = mode;
          }
        }),

      setSidebarCollapsed: (collapsed: boolean) =>
        set((state) => {
          state.settings.navigation.sidebar.collapsed = collapsed;
          state.settings.updatedAt = new Date().toISOString();
        }),

      setSidebarSearchQuery: (query: string) =>
        set((state) => {
          state.settings.navigation.sidebar.searchQuery = query;
        }),

      toggleFavorite: (itemId: string) =>
        set((state) => {
          const favorites = state.settings.navigation.sidebar.favorites;
          const index = favorites.indexOf(itemId);
          if (index > -1) {
            favorites.splice(index, 1);
          } else {
            favorites.push(itemId);
          }
          state.settings.updatedAt = new Date().toISOString();
        }),

      setActiveModuleId: (moduleId: string | null) =>
        set((state) => {
          state.settings.navigation.sidebar.activeModuleId = moduleId;
          state.settings.updatedAt = new Date().toISOString();
        }),

      setBreadcrumbsExpanded: (expanded: boolean) =>
        set((state) => {
          state.settings.navigation.breadcrumbs.expanded = expanded;
          state.settings.updatedAt = new Date().toISOString();
        }),

      setGlobalPageSize: (pageSize: number) =>
        set((state) => {
          state.settings.tables.global.defaultPageSize = pageSize;
          state.settings.updatedAt = new Date().toISOString();
        }),

      setGlobalSort: (sort: SortConfig) =>
        set((state) => {
          state.settings.tables.global.defaultSort = sort;
          state.settings.updatedAt = new Date().toISOString();
        }),

      setShowRowNumbers: (showRowNumbers: boolean) =>
        set((state) => {
          state.settings.tables.global.showRowNumbers = showRowNumbers;
          state.settings.updatedAt = new Date().toISOString();
        }),

      setStickyHeader: (stickyHeader: boolean) =>
        set((state) => {
          state.settings.tables.global.stickyHeader = stickyHeader;
          state.settings.updatedAt = new Date().toISOString();
        }),

      setTableAnimation: (animation: boolean) =>
        set((state) => {
          state.settings.tables.global.animation = animation;
          state.settings.updatedAt = new Date().toISOString();
        }),

      setTableDensity: (density: TableDensity) =>
        set((state) => {
          state.settings.tables.global.density = density;
          state.settings.updatedAt = new Date().toISOString();
        }),

      setBadgeSize: (badgeSize: BadgeSize) =>
        set((state) => {
          state.settings.tables.global.badgeSize = badgeSize;
          state.settings.updatedAt = new Date().toISOString();
        }),

      setIconSize: (iconSize: IconSize) =>
        set((state) => {
          state.settings.tables.global.iconSize = iconSize;
          state.settings.updatedAt = new Date().toISOString();
        }),

      setModuleTableConfig: (module: string, config: Partial<TableConfig>) =>
        set((state) => {
          state.settings.tables.perModule[module] = {
            ...state.settings.tables.perModule[module],
            ...config,
          };
          state.settings.updatedAt = new Date().toISOString();
        }),

      addRecentFilter: (filter: FilterEntry) =>
        set((state) => {
          const recent = state.settings.filters.recent;
          // Remove duplicates (same module + path)
          const filtered = recent.filter(
            (f) => !(f.module === filter.module && f.path === filter.path),
          );
          // Keep only last 10
          state.settings.filters.recent = [filter, ...filtered].slice(0, 10);
          state.settings.updatedAt = new Date().toISOString();
        }),

      setRecentFilters: (filters: FilterEntry[]) =>
        set((state) => {
          state.settings.filters.recent = filters.slice(0, 10);
          state.settings.updatedAt = new Date().toISOString();
        }),

      setRecentPages: (pages: RecentPage[]) =>
        set((state) => {
          state.settings.recentPages = pages;
          state.settings.updatedAt = new Date().toISOString();
        }),

      setDashboardLayout: (layout: DashboardLayout) =>
        set((state) => {
          state.settings.dashboard.layout = layout;
          state.settings.updatedAt = new Date().toISOString();
        }),

      setDashboardPeriod: (period: DashboardPeriod) =>
        set((state) => {
          state.settings.dashboard.period = period;
          state.settings.updatedAt = new Date().toISOString();
        }),

      setWidgetConfig: (widgetId: string, config: Partial<WidgetConfig>) =>
        set((state) => {
          state.settings.dashboard.widgets[widgetId] = {
            ...state.settings.dashboard.widgets[widgetId],
            ...config,
          };
          state.settings.updatedAt = new Date().toISOString();
        }),

      setFontSize: (fontSize: FontSize) =>
        set((state) => {
          state.settings.accessibility.fontSize = fontSize;
          state.settings.updatedAt = new Date().toISOString();
        }),

      setReducedMotion: (reducedMotion: boolean) =>
        set((state) => {
          state.settings.accessibility.reducedMotion = reducedMotion;
          state.settings.updatedAt = new Date().toISOString();
        }),

      setHighContrast: (highContrast: boolean) =>
        set((state) => {
          state.settings.accessibility.highContrast = highContrast;
          state.settings.updatedAt = new Date().toISOString();
        }),

      setFocusVisible: (focusVisible: boolean) =>
        set((state) => {
          state.settings.accessibility.focusVisible = focusVisible;
          state.settings.updatedAt = new Date().toISOString();
        }),

      setNotificationSound: (sound: boolean) =>
        set((state) => {
          state.settings.notifications.sound = sound;
          state.settings.updatedAt = new Date().toISOString();
        }),

      setNotificationDesktop: (desktop: boolean) =>
        set((state) => {
          state.settings.notifications.desktop = desktop;
          state.settings.updatedAt = new Date().toISOString();
        }),

      setNotificationDuration: (duration: number) =>
        set((state) => {
          state.settings.notifications.duration = duration;
          state.settings.updatedAt = new Date().toISOString();
        }),

      setModuleNotification: (module: string, enabled: boolean) =>
        set((state) => {
          state.settings.notifications.modules[module] = enabled;
          state.settings.updatedAt = new Date().toISOString();
        }),

      setLastPersonneId: (personneId: number | null) =>
        set((state) => {
          state.settings.planning.lastPersonneId = personneId;
          state.settings.updatedAt = new Date().toISOString();
        }),

      reset: () =>
        set((state) => {
          Object.assign(state.settings, defaultUISettings);
          state.settings.updatedAt = new Date().toISOString();
        }),
    })),
    {
      name: STORAGE_KEYS.UI.SIDEBAR,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ settings: state.settings }),
      onRehydrateStorage: () => (state) => {
        // Run migration after state is rehydrated
        if (!state?.settings) return;
        if (state.settings.version === undefined || state.settings.version < 1) {
          // Migrate from old 'theme' key
          const oldTheme = localStorage.getItem(STORAGE_KEYS.UI.THEME);
          if (oldTheme) {
            const isDark = oldTheme.includes("dark");
            state.settings.theme = {
              mode: isDark ? "dark" : "light",
              resolved: isDark ? "dark" : "light",
            };
            state.settings.version = 1;
            state.settings.updatedAt = new Date().toISOString();
            localStorage.removeItem(STORAGE_KEYS.UI.THEME);
          }
        }
      },
    },
  ),
);
